import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { useAuth } from "../../../../context/AuthContext";
import {
  obtenerAsambleas,
  AsambleaRecord,
} from "../../../../api/asambleaService";
import { HUB_BASE_URL } from "../../../../api/signalRConfig";
import Swal from "sweetalert2";
import {
  Users,
  RefreshCcw,
  Search,
  AlertCircle,
  Calendar,
  Plus,
  FileText,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  UserCheck,
} from "lucide-react";
import DetailAsambleaModal from "./DetailAsambleaModal";
import CreateAsambleaModal from "./CreateAsambleaModal";
import PreguntasAsambleaModal from "./PreguntasAsambleaModal";
import ParticipantesAsambleaModal from "./ParticipantesAsambleaModal";

const ALLOWED_ROLES = ["AD", "AS"];

const statusStyles: Record<
  string,
  { text: string; badge: string; pill: string }
> = {
  N: {
    text: "Nueva",
    badge: "text-blue-600 bg-blue-50 border border-blue-200",
    pill: "bg-blue-100 text-blue-700",
  },
  A: {
    text: "Abierta",
    badge: "text-green-600 bg-green-50 border border-green-200",
    pill: "bg-green-100 text-green-700",
  },
  C: {
    text: "Cerrada",
    badge: "text-gray-600 bg-gray-50 border border-gray-200",
    pill: "bg-gray-100 text-gray-700",
  },
  E: {
    text: "En curso",
    badge: "text-orange-600 bg-orange-50 border border-orange-200",
    pill: "bg-orange-100 text-orange-700",
  },
};

const getStatusStyle = (estatus: string) => {
  const key = (estatus || "").trim().charAt(0).toUpperCase();
  return (
    statusStyles[key] || {
      text: estatus || "Sin estado",
      badge: "text-gray-600 bg-gray-50 border border-gray-200",
      pill: "bg-gray-100 text-gray-700",
    }
  );
};

const formatDate = (value: string) => {
  if (!value || value.trim() === "") return "Sin fecha";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

export default function AsambleaListPage() {
  const { session } = useAuth();
  const unidadCodigo = session?.unidad?.id || "";
  const rolCodigo = (session?.rol?.id || "").toUpperCase();
  const isAuthorized = ALLOWED_ROLES.includes(rolCodigo);

  const [isLoading, setIsLoading] = useState(false);
  const [asambleas, setAsambleas] = useState<AsambleaRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAsamblea, setSelectedAsamblea] = useState<AsambleaRecord | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPreguntasModalOpen, setIsPreguntasModalOpen] = useState(false);
  const [asambleaForPreguntas, setAsambleaForPreguntas] = useState<AsambleaRecord | null>(null);
  const [isParticipantesModalOpen, setIsParticipantesModalOpen] = useState(false);
  const [asambleaForParticipantes, setAsambleaForParticipantes] = useState<AsambleaRecord | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [codigoFilter, setCodigoFilter] = useState("");
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const fetchAsambleasRef = useRef<((silent?: boolean) => Promise<void>) | null>(null);

  const fetchAsambleas = useCallback(async (silent = false) => {
    if (!unidadCodigo) {
      return;
    }

    if (!silent) {
      setIsLoading(true);
    }

    try {
      const response = await obtenerAsambleas({
        "unidad residencial codigo": unidadCodigo,
        codigo: codigoFilter.trim(),
      });

      if (response.code === "1" && Array.isArray(response.object_)) {
        setAsambleas(response.object_);
      } else {
        setAsambleas([]);
      }

      setLastUpdated(new Date());
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "No se pudieron cargar las asambleas";

      Swal.fire({
        icon: "error",
        title: "No se pudo cargar la información",
        text: message,
        confirmButtonColor: "#3d5a40",
      });
    } finally {
      setIsLoading(false);
    }
  }, [unidadCodigo, codigoFilter]);

  fetchAsambleasRef.current = fetchAsambleas;

  useEffect(() => {
    if (isAuthorized && unidadCodigo) {
      fetchAsambleas(true);
    }
  }, [isAuthorized, unidadCodigo, fetchAsambleas]);

  useEffect(() => {
    if (!isAuthorized || !unidadCodigo) return;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${HUB_BASE_URL}/asambleahub`, { withCredentials: true })
      .withAutomaticReconnect()
      .build();

    connection.on("cargarAsambleas", () => {
      fetchAsambleasRef.current?.(false);
    });

    const start = async () => {
      try {
        await connection.start();
        await connection.invoke("UnirseAGrupo", unidadCodigo);
      } catch (err) {
        if (err instanceof Error && process.env.NODE_ENV === "development") {
          console.error("[AsambleaHub]", err.message);
        }
      }
    };

    start();

    return () => {
      if (connection.state !== signalR.HubConnectionState.Disconnected) {
        connection.stop();
      }
    };
  }, [isAuthorized, unidadCodigo]);

  const handleClearFilters = () => {
    setCodigoFilter("");
    setSearchTerm("");
    fetchAsambleas(false);
  };

  const handleAsambleaClick = (asamblea: AsambleaRecord) => {
    setSelectedAsamblea(asamblea);
    setIsModalOpen(true);
  };

  const handleCloseModal = (shouldReload?: boolean) => {
    setIsModalOpen(false);
    setSelectedAsamblea(null);
    if (shouldReload) {
      fetchAsambleas(false);
    }
  };

  const handlePreguntasClick = (e: React.MouseEvent, asamblea: AsambleaRecord) => {
    e.stopPropagation();
    setAsambleaForPreguntas(asamblea);
    setIsPreguntasModalOpen(true);
  };

  const handleClosePreguntasModal = () => {
    setIsPreguntasModalOpen(false);
    setAsambleaForPreguntas(null);
  };

  const handleParticipantesClick = (e: React.MouseEvent, asamblea: AsambleaRecord) => {
    e.stopPropagation();
    setAsambleaForParticipantes(asamblea);
    setIsParticipantesModalOpen(true);
  };

  const handleCloseParticipantesModal = () => {
    setIsParticipantesModalOpen(false);
    setAsambleaForParticipantes(null);
  };

  const filteredAsambleas = useMemo(() => {
    if (!searchTerm.trim()) return asambleas;

    const term = searchTerm.toLowerCase();
    return asambleas.filter((item) => {
      const valuesToCheck = [
        item.asa_codigo?.toString(),
        item.asa_titulo,
        item.asa_descripcion,
        item.asa_estatus_nombre,
        item.asa_administrador_nombre,
        item.asa_moderador_nombre,
        item.asa_unidad_residencial_nombre,
      ];

      return valuesToCheck.some((value) =>
        (value || "").toLowerCase().includes(term)
      );
    });
  }, [asambleas, searchTerm]);

  const summary = useMemo(() => {
    const nuevas = asambleas.filter(
      (item) => (item.asa_estatus || "").toUpperCase() === "N"
    ).length;

    const abiertas = asambleas.filter(
      (item) => (item.asa_estatus || "").toUpperCase() === "A"
    ).length;

    const cerradas = asambleas.filter(
      (item) => (item.asa_estatus || "").toUpperCase() === "C"
    ).length;

    const enCurso = asambleas.filter(
      (item) => (item.asa_estatus || "").toUpperCase() === "E"
    ).length;

    return {
      total: asambleas.length,
      nuevas,
      abiertas,
      cerradas,
      enCurso,
    };
  }, [asambleas]);

  const handleCreateAsamblea = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = (created: boolean) => {
    setIsCreateModalOpen(false);
    if (created) {
      fetchAsambleas(false);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar />
        <main className="flex-1 p-6 md:ml-64 flex items-center justify-center">
          <Card className="max-w-lg shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle size={24} />
                Acceso restringido
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Esta sección está disponible únicamente para los roles
                Administrador (AD) y Socio Administrador (AS). Si necesitas acceso,
                contacta al equipo de EasyPH.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all space-y-4 sm:space-y-6">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-military-600">
            Gestión de Asambleas
          </h1>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            {lastUpdated && (
              <span className="text-xs sm:text-sm text-gray-500">
                Actualizado: {lastUpdated.toLocaleString()}
              </span>
            )}
            <Button
              className="gap-2 bg-military-500 hover:bg-military-600 text-white"
              onClick={handleCreateAsamblea}
            >
              <Plus size={18} />
              Nueva Asamblea
            </Button>
            <Button
              variant="outline"
              className="gap-2 w-full sm:w-auto"
              onClick={() => fetchAsambleas()}
              disabled={isLoading}
            >
              <RefreshCcw
                size={18}
                className={isLoading ? "animate-spin" : ""}
              />
              Actualizar
            </Button>
          </div>
        </header>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-military-500 shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Asambleas</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {summary.total}
                  </p>
                </div>
                <div className="bg-military-100 p-3 rounded-full">
                  <Users size={24} className="text-military-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-blue-500 shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Nuevas</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {summary.nuevas}
                  </p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <FileText size={24} className="text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-green-500 shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Abiertas</p>
                  <p className="text-3xl font-bold text-green-600">
                    {summary.abiertas}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <Users size={24} className="text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-gray-500 shadow-md">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Cerradas</p>
                  <p className="text-3xl font-bold text-gray-600">
                    {summary.cerradas}
                  </p>
                </div>
                <div className="bg-gray-100 p-3 rounded-full">
                  <FileText size={24} className="text-gray-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <Card className="shadow-lg">
          <CardHeader 
            className="flex flex-row items-center justify-between border-b cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          >
            <div className="flex flex-col gap-2 flex-1">
              <div className="flex items-center gap-2 text-military-600">
                <Search size={20} />
                <CardTitle>Búsqueda y filtros</CardTitle>
              </div>
              <p className="text-sm text-gray-500">
                Busca asambleas por título, descripción, código o estado.
              </p>
            </div>
            <div className="ml-4">
              {isFiltersExpanded ? (
                <ChevronUp size={24} className="text-gray-500" />
              ) : (
                <ChevronDown size={24} className="text-gray-500" />
              )}
            </div>
          </CardHeader>
          {isFiltersExpanded && (
            <CardContent className="space-y-4 pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Código de Asamblea
                  </label>
                  <Input
                    placeholder="Ej: 1"
                    value={codigoFilter}
                    onChange={(e) => setCodigoFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Search size={16} />
                    Búsqueda general
                  </label>
                  <Input
                    placeholder="Buscar por título, descripción..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-4">
                <div className="flex gap-2">
                  <Button
                    className="bg-military-500 hover:bg-military-600 text-white"
                    onClick={() => fetchAsambleas()}
                    disabled={isLoading}
                  >
                    Consultar
                  </Button>
                  <Button variant="outline" onClick={handleClearFilters}>
                    Limpiar
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        <Card className="shadow-lg">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <Users size={20} />
              Lista de Asambleas ({filteredAsambleas.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
                <p className="text-gray-600">Cargando asambleas...</p>
              </div>
            ) : filteredAsambleas.length === 0 ? (
              <div className="text-center py-16">
                <Users
                  size={48}
                  className="mx-auto text-gray-400 mb-4"
                />
                <p className="text-gray-600 font-medium">
                  No se encontraron registros
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Ajusta los filtros o actualiza la información para ver
                  nuevas asambleas.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {filteredAsambleas.map((item) => {
                  const style = getStatusStyle(item.asa_estatus);
                  return (
                    <div
                      key={item.asa_codigo}
                      className="w-full rounded-2xl border bg-white transition shadow-sm hover:shadow-lg"
                    >
                      <div className="flex gap-4 p-4">
                        <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center border">
                          <Users
                            size={32}
                            className="text-gray-400"
                          />
                        </div>
                        <div className="flex-1 flex flex-col gap-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-gray-500">
                              #{item.asa_codigo}
                            </span>
                            <span
                              className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${style.badge}`}
                            >
                              {item.asa_estatus_nombre || style.text}
                            </span>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {item.asa_titulo}
                          </h3>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {item.asa_descripcion || "Sin descripción"}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              Apertura: {formatDate(item.asa_fecha_apertura)}
                            </span>
                            <span className="font-mono text-gray-700">
                              Llave: {item.asa_llave}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 px-4 pb-4">
                        <Button
                          onClick={() => handleAsambleaClick(item)}
                          variant="outline"
                          className="w-full border-military-500 text-military-600 hover:bg-military-50"
                        >
                          <FileText size={16} className="mr-2" />
                          Ver Detalle
                        </Button>
                        <Button
                          onClick={(e) => handlePreguntasClick(e, item)}
                          variant="outline"
                          className="w-full border-blue-500 text-blue-600 hover:bg-blue-50"
                        >
                          <MessageSquare size={16} className="mr-2" />
                          Preguntas
                        </Button>
                        <Button
                          onClick={(e) => handleParticipantesClick(e, item)}
                          variant="outline"
                          className="w-full border-purple-500 text-purple-600 hover:bg-purple-50"
                        >
                          <UserCheck size={16} className="mr-2" />
                          Participantes
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <DetailAsambleaModal
          isOpen={isModalOpen}
          asamblea={selectedAsamblea}
          onClose={handleCloseModal}
        />

        <CreateAsambleaModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
        />

        <PreguntasAsambleaModal
          isOpen={isPreguntasModalOpen}
          asamblea={asambleaForPreguntas}
          onClose={handleClosePreguntasModal}
        />

        <ParticipantesAsambleaModal
          isOpen={isParticipantesModalOpen}
          asamblea={asambleaForParticipantes}
          onClose={handleCloseParticipantesModal}
        />
      </main>
    </div>
  );
}
